/**
 * Domain + wire types shared by the API Worker, the stream Worker and the SPA.
 *
 * Everything in this package must be runtime-agnostic: it is bundled into two
 * Workers *and* into a browser bundle, so it may not import node: builtins, and
 * it may only use APIs present in both the Workers runtime and the browser
 * (fetch, URL, TextEncoder, crypto.subtle, atob/btoa).
 */

/** The three catalog kinds. `movie`/`series` mirror Xtream's own vocabulary. */
export type Kind = 'live' | 'movie' | 'series';

export const KINDS: readonly Kind[] = ['live', 'movie', 'series'] as const;

export function isKind(value: unknown): value is Kind {
  return value === 'live' || value === 'movie' || value === 'series';
}

/** What the account owner asked us to pull. Nothing is pulled by default. */
export interface IngestSelection {
  live: boolean;
  movies: boolean;
  series: boolean;
}

/** The account's preferred LIVE output. Persisted, and CHECK-constrained in D1. */
export type OutputFormat = 'hls' | 'ts';

/**
 * How the browser should actually play a resolved source.
 *
 * `file` is the one most likely to be got wrong: Xtream serves VOD as a
 * progressive `/movie/u/p/id.mkv`, which is NOT HLS. Handing that to hls.js
 * fails every time, so it has to be a distinct case from `hls`.
 */
export type PlaybackFormat = 'hls' | 'ts' | 'file';

export type AccountStatus = 'unknown' | 'active' | 'expired' | 'banned' | 'unreachable';

/** The two published iptv-org playlists a public import can point at. */
export type PresetImportScope = 'us' | 'all';

/**
 * Which playlist a public import plays: one of the published iptv-org files, or
 * `custom`, where the playlist address is whatever the administrator entered
 * and is stored in the account's `serverUrl`.
 */
export type ImportScope = PresetImportScope | 'custom';

/**
 * Official playlist URLs from iptv-org's own playlists page.
 * US Only is the United States file. All Countries is the full public index.
 */
export const IPTV_ORG_PLAYLIST_URL: Record<PresetImportScope, string> = {
  us: 'https://iptv-org.github.io/iptv/countries/us.m3u',
  all: 'https://iptv-org.github.io/iptv/index.m3u',
};

export const IMPORT_SCOPE_LABEL: Record<ImportScope, string> = {
  us: 'US Only',
  all: 'All Countries',
  custom: 'Custom URL',
};

export function isImportScope(value: unknown): value is ImportScope {
  return value === 'us' || value === 'all' || value === 'custom';
}

/** The preset a playlist address is exactly equal to, or null for any other address. */
export function presetScopeForUrl(url: string): PresetImportScope | null {
  const trimmed = url.trim();
  for (const scope of ['us', 'all'] as const) {
    if (IPTV_ORG_PLAYLIST_URL[scope] === trimmed) return scope;
  }
  return null;
}

/** Account as projected to the browser. The password never appears here. */
export interface AccountSummary {
  id: string;
  label: string;
  serverUrl: string;
  /** Set when this line plays a published iptv-org playlist and has no panel login. */
  importScope: ImportScope | null;
  username: string;
  userAgent: string;
  ingest: IngestSelection;
  /** null = no country filter has been chosen yet (show everything). */
  liveCountryCodes: string[] | null;
  includeOther: boolean;
  outputFormat: OutputFormat;
  status: AccountStatus;
  expiresAt: number | null;
  maxConnections: number | null;
  allowedFormats: string[] | null;
  createdAt: number;
  updatedAt: number;
  lastIngestAt: number | null;
  counts: Partial<Record<Kind, number>>;
}

export interface CategoryDto {
  id: string;
  name: string;
  kind: Kind;
  /** Effective code: the override when one is pinned, else the parser's guess. */
  countryCode: string | null;
  /**
   * What the parser made of the name, independent of any override — so the UI
   * can offer "go back to the guess" and say what the guess actually was.
   */
  countryParsed: string | null;
  /** True when a human pinned the code, so the UI can show it differently. */
  countryPinned: boolean;
  hidden: boolean;
  streamCount: number;
}

export interface StreamDto {
  id: number;
  kind: Kind;
  name: string;
  categoryId: string | null;
  countryCode: string | null;
  logoUrl: string | null;
  container: string | null;
  rating: number | null;
  year: number | null;
  addedAt: number | null;
  hasArchive: boolean;
  favorite: boolean;
  /** Seconds; only present for movie/series rows the user has started. */
  positionS?: number;
  durationS?: number;
  extra?: Record<string, unknown>;
}

export interface Page<T> {
  items: T[];
  /** Opaque; pass back as `cursor`. null when the last page was returned. */
  nextCursor: string | null;
  total?: number;
}

export interface CountryBucket {
  /** null is the "Other / unmatched" bucket. */
  code: string | null;
  label: string;
  count: number;
}

// ---------------------------------------------------------------------------
// Ingest
// ---------------------------------------------------------------------------

export type IngestStatus = 'queued' | 'running' | 'done' | 'error' | 'canceled';

export interface IngestProgress {
  jobId: string;
  accountId: string;
  kind: Kind;
  status: IngestStatus;
  categoriesTotal: number;
  categoriesDone: number;
  streamsWritten: number;
  error: string | null;
  startedAt: number;
  finishedAt: number | null;
}

/** Queue message. Kept small — Queues cap a message at 128 KiB. */
export interface IngestMessage {
  v: 1;
  jobId: string;
  accountId: string;
  kind: Kind;
  /**
   * `categories` discovers and fans out; `streams` ingests one slice of
   * category ids; `finalize` writes counts and closes the job.
   */
  step: 'categories' | 'streams' | 'finalize';
  /** For step='streams': the category ids this message owns. */
  categoryIds?: string[];
  /** Monotonic, for progress reporting. */
  batchIndex?: number;
  batchTotal?: number;
}

// ---------------------------------------------------------------------------
// Playback
// ---------------------------------------------------------------------------

export interface PlayRequest {
  accountId: string;
  kind: Kind;
  xtreamId: number;
  /** Series only: the episode's own panel id. */
  episodeId?: string;
  /** Series/movie: container extension when the caller already knows it. */
  container?: string;
  /** Force a format instead of the account default. */
  format?: OutputFormat;
}

export interface PlayResponse {
  /** Absolute URL on the stream Worker. Always proxied, never the panel. */
  url: string;
  /** How the browser should play it. */
  format: PlaybackFormat;
  kind: Kind;
  title: string;
  logoUrl: string | null;
  /** Epoch seconds at which `url` stops working. */
  expiresAt: number;
  /** Seconds; 0 when there is nothing to resume. */
  resumeFrom: number;
  durationS: number | null;
}

// ---------------------------------------------------------------------------
// User state
// ---------------------------------------------------------------------------

export interface ProgressUpdate {
  accountId: string;
  kind: Kind;
  xtreamId: number;
  episodeId?: string;
  positionS: number;
  durationS?: number;
}

export interface FavoriteUpdate {
  accountId: string;
  kind: Kind;
  xtreamId: number;
  favorite: boolean;
}

export interface ContinueWatchingItem extends StreamDto {
  accountId: string;
  accountLabel: string;
  episodeId: string | null;
  positionS: number;
  durationS: number;
  updatedAt: number;
}

// ---------------------------------------------------------------------------
// Free channels
//
// Publicly available live channels seeded from the iptv-org community lists,
// not tied to any Xtream account -- there is no panel login behind them, so
// every signed-in user sees the same shared library.
// ---------------------------------------------------------------------------

export interface FreeChannelDto {
  id: string;
  name: string;
  category: string;
  logoUrl: string | null;
}

export interface FreeCategoryDto {
  slug: string;
  label: string;
  count: number;
}

/**
 * One row of iptv-org's published United States playlist, without the stream
 * address. Playback is minted separately so the browser never submits a URL.
 */
export interface UsaTestChannelDto {
  id: string;
  name: string;
  group: string;
  logoUrl: string | null;
  /** False when this player cannot open the format, for example MPEG-DASH. */
  playable: boolean;
  skipReason: string | null;
}

export interface UsaTestPlaylistDto {
  playlistUrl: string;
  /** Epoch seconds, when this Worker last read the published playlist. */
  fetchedAt: number;
  channelCount: number;
  channels: UsaTestChannelDto[];
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export type ApiErrorCode =
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'bad_request'
  | 'conflict'
  | 'rate_limited'
  | 'panel_unreachable'
  | 'panel_rejected'
  | 'panel_expired'
  | 'panel_ip_literal'
  | 'ingest_failed'
  | 'internal';

export interface ApiErrorBody {
  error: ApiErrorCode;
  message: string;
  /** Safe-to-display detail. Never contains credentials. */
  detail?: string;
}

export class ApiError extends Error {
  constructor(
    readonly code: ApiErrorCode,
    message: string,
    readonly status: number,
    readonly detail?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  toBody(): ApiErrorBody {
    return { error: this.code, message: this.message, ...(this.detail ? { detail: this.detail } : {}) };
  }

  static badRequest(message: string, detail?: string) {
    return new ApiError('bad_request', message, 400, detail);
  }
  static unauthorized(message = 'Sign in required') {
    return new ApiError('unauthorized', message, 401);
  }
  static forbidden(message = 'Not allowed') {
    return new ApiError('forbidden', message, 403);
  }
  static notFound(message = 'Not found') {
    return new ApiError('not_found', message, 404);
  }
  static conflict(message: string) {
    return new ApiError('conflict', message, 409);
  }
  static internal(message = 'Something went wrong') {
    return new ApiError('internal', message, 500);
  }
}

// ---------------------------------------------------------------------------
// Error log
// ---------------------------------------------------------------------------

/** Where a reported error originated: the browser player, or the stream Worker. */
export type ErrorLogSource = 'player' | 'stream';

export interface ErrorLogEntry {
  id: string;
  accountId: string | null;
  source: ErrorLogSource;
  code: string;
  message: string;
  detail?: string;
  createdAt: number;
}

/** What a reporter sends; the route stamps `source`, `id` and `createdAt` itself. */
export interface ReportErrorInput {
  accountId?: string;
  code: string;
  message: string;
  detail?: string;
}

// ---------------------------------------------------------------------------
// Presence
// ---------------------------------------------------------------------------

/** What the player sends every ~20s while a Watch session is open. */
export interface PresenceHeartbeat {
  accountId: string;
  kind: Kind;
  xtreamId: number;
  episodeId?: string;
  title: string;
}

/** One row of "who is currently streaming, and what" — admin-only. */
export interface ActiveViewer {
  userId: string;
  email: string;
  accountId: string;
  accountLabel: string;
  kind: Kind;
  xtreamId: number;
  episodeId: string | null;
  title: string;
  startedAt: number;
  lastSeenAt: number;
}
